import { useEffect, useRef, useState } from 'react'
import {
  CheckCircle2,
  ChevronDown,
  Clock,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { BatchFailurePanel } from '@/components/custom/deal-review/batch-failure-panel'
import { cn } from '@/lib/utils'
import { extractErrorMessage, toast } from '@/lib/toast'
import {
  useActiveSendBatches,
  startedBatchIds,
  useRetrySendBatch,
  useSendBatchDetail,
  useSendStartCount,
} from '@/hooks/use-deal-send-batches'
import { relativeTime } from '@/features/deal-review/intake-summary'
import {
  MUTED_TEXT,
  PANEL_ACTION,
  PILL_TONE,
} from '@/features/deal-review/review-tones'
import {
  completionMessage,
  dismissBatch,
  dismissedBatchIds,
  ITEM_STATUS_LABEL,
  retriedMessage,
  SEND_TOAST_POSITION,
  sendStatusPill,
  type PillState,
} from '@/features/deal-review/send-batch'
import type { RetrySendBatch, SendBatchItemStatus } from '@/types/api'

const PILL_ICON: Record<PillState, LucideIcon> = {
  sending: RefreshCw,
  'sending-queued': RefreshCw,
  queued: Clock,
  finished: CheckCircle2,
  failures: TriangleAlert,
  stopped: TriangleAlert,
}

const TITLE: Record<PillState, string> = {
  sending: 'Sending to DealCloud',
  'sending-queued': 'Sending to DealCloud',
  queued: 'Queued for DealCloud',
  finished: 'Send finished',
  failures: 'Send finished with failures',
  stopped: 'Send stopped',
}

const TERMINAL: PillState[] = ['finished', 'failures', 'stopped']

const ITEM_TONE: Record<SendBatchItemStatus, string> = {
  QUEUED: MUTED_TEXT,
  RUNNING: 'font-medium text-primary',
  SENT: 'text-primary',
  FAILED: 'text-destructive',
  DUPLICATE: MUTED_TEXT,
  SKIPPED: MUTED_TEXT,
}

// Long enough to read "Sending 0 of N", short enough not to sit over the toolbar.
const AUTO_OPEN_MS = 4_000

const deals = (n: number) => `${n} ${n === 1 ? 'deal' : 'deals'}`

export function DealCloudSendStatus() {
  const active = useActiveSendBatches()
  const retry = useRetrySendBatch()
  const [open, setOpen] = useState(false)
  const [dismissed, setDismissed] = useState(dismissedBatchIds)
  const data = active.data
  const view = data ? sendStatusPill(data, dismissed) : null
  const live = view !== null && !TERMINAL.includes(view.state)
  const leadList = useSendBatchDetail(
    view?.batch.id ?? '',
    open && !!view,
    live
  )
  const items = leadList.data?.items
  const refetchDetail = leadList.refetch
  const batchId = view?.batch.id
  const wasLive = useRef(live)
  // Polling stops with the batch, so fetch once more for its final per-lead states.
  // Only on the running -> finished edge: reopening a finished batch already fetches.
  useEffect(() => {
    if (open && batchId && wasLive.current && !live) void refetchDetail()
    wasLive.current = live
  }, [live, open, batchId, refetchDetail])

  // Opens when this tab starts a batch, then closes unless the user is on it.
  const started = useSendStartCount()
  const [seenStart, setSeenStart] = useState(started)
  const [autoOpened, setAutoOpened] = useState(false)
  const held = useRef(false)
  if (started !== seenStart) {
    setSeenStart(started)
    setAutoOpened(true)
    setOpen(true)
  }
  useEffect(() => {
    if (!autoOpened) return
    const timer = setTimeout(() => {
      if (!held.current) setOpen(false)
    }, AUTO_OPEN_MS)
    return () => clearTimeout(timer)
  }, [autoOpened, started])
  const hold = {
    onPointerEnter: () => {
      held.current = true
    },
    onPointerLeave: () => {
      held.current = false
    },
    onFocus: () => {
      held.current = true
    },
    onBlur: () => {
      held.current = false
    },
  }

  // Toasts once when a batch this tab started finishes.
  useEffect(() => {
    const done = data?.lastFinished
    if (!data || !done || !startedBatchIds.delete(done.id)) return
    if (dismissedBatchIds().includes(done.id)) return
    const finished = sendStatusPill({ ...data, running: null, queued: [] }, [])
    if (!finished) return
    const notify = finished.state === 'finished' ? toast.success : toast.warning
    notify(completionMessage(finished), {
      id: `dealcloud-finished-${done.id}`,
      position: SEND_TOAST_POSITION,
    })
  }, [data])

  // Hidden until the first fetch lands, and when a first fetch fails.
  if (!data || !view) return null

  const { batch, state } = view
  const { canSend, queued } = data
  const stale = active.isError
  const now = active.dataUpdatedAt
  const terminal = TERMINAL.includes(state)
  const Icon = stale ? TriangleAlert : PILL_ICON[state]
  const spinning = !stale && (state === 'sending' || state === 'sending-queued')
  const rest = batch.skipped

  const retryAs = (scope: RetrySendBatch['scope']) =>
    retry.mutate(
      { id: batch.id, scope },
      {
        onSuccess: (result) =>
          toast.success(retriedMessage(result), {
            position: SEND_TOAST_POSITION,
          }),
        onError: (error) =>
          toast.error(
            extractErrorMessage(error) ?? 'That could not be queued again.',
            { position: SEND_TOAST_POSITION }
          ),
      }
    )

  const dismiss = () => {
    dismissBatch(batch.id)
    setDismissed([batch.id])
    setOpen(false)
  }

  // Queued only: the header already describes the first batch, so it is not listed again.
  const waiting = queued
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.id !== batch.id)

  const detail = [
    `Started by ${batch.startedBy.name}`,
    relativeTime(batch.startedAt ?? batch.createdAt, now),
    ...(terminal && batch.finishedAt
      ? [`finished ${relativeTime(batch.finishedAt, now)}`]
      : []),
  ].join(' · ')

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setAutoOpened(false)
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="DealCloud send status"
          {...hold}
          className={cn(
            'relative inline-flex cursor-pointer items-center overflow-hidden gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors',
            PILL_TONE[stale ? 'warning' : view.tone]
          )}
        >
          <Icon
            aria-hidden="true"
            className={cn(
              'size-3.5 shrink-0 opacity-70',
              spinning && 'motion-safe:animate-spin'
            )}
          />
          <span className="font-medium">DealCloud</span>
          <span aria-hidden="true" className="opacity-40">
            ·
          </span>
          <span className="max-w-56 truncate">
            {stale ? `${view.label} · not updating` : view.label}
          </span>
          <ChevronDown
            aria-hidden="true"
            className={cn('size-3 transition-transform', open && 'rotate-180')}
          />
          {spinning && batch.total > 0 && (
            <span
              aria-hidden="true"
              data-testid="send-progress"
              className="absolute inset-x-0 bottom-0 h-0.5 bg-current/10"
            >
              <span
                className="block h-full bg-current/50 motion-safe:transition-all motion-safe:duration-500"
                // A runtime fraction: no scale class can carry it.
                style={{ width: `${(batch.done / batch.total) * 100}%` }}
              />
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-120 max-w-[calc(100vw-2rem)] p-0"
        {...hold}
        // Auto-opened: leave focus where the user is, both ways.
        onOpenAutoFocus={(event) => {
          if (autoOpened) event.preventDefault()
        }}
        onCloseAutoFocus={(event) => {
          if (autoOpened) event.preventDefault()
          setAutoOpened(false)
        }}
      >
        <div className="flex items-start justify-between gap-3 px-3.5 pb-3 pt-3.5">
          <div className="min-w-0 space-y-1">
            <Typography
              as="p"
              variant="body-sm"
              className="text-sm font-medium leading-tight"
            >
              {TITLE[state]}
            </Typography>
            <Typography
              as="p"
              variant="body-sm"
              className="text-xs leading-tight text-muted-foreground"
            >
              {detail}
            </Typography>
            {state !== 'queued' && !terminal && (
              <Typography
                as="p"
                variant="body-sm"
                className="text-xs leading-tight text-muted-foreground"
              >
                {`${batch.done} of ${batch.total} done.`}
                {batch.failed > 0 && ` ${batch.failed} failed so far.`}
              </Typography>
            )}
          </div>
          {terminal && (
            <Button
              variant="ghost"
              size="sm"
              onClick={dismiss}
              className={cn(PANEL_ACTION, '-mr-1.5 -mt-0.5 h-6 px-2 text-xs')}
            >
              Dismiss
            </Button>
          )}
        </div>

        {items && items.length > 0 && (
          <ul
            aria-label="Deals in this send"
            className="max-h-60 overflow-y-auto border-t"
          >
            {items.map((item) => (
              <li
                key={item.leadId}
                className="flex items-center justify-between gap-3 px-3.5 py-1.5"
              >
                <Typography
                  as="span"
                  variant="body-sm"
                  className="min-w-0 truncate text-xs text-primary"
                >
                  {item.label}
                </Typography>
                <Typography
                  as="span"
                  variant="body-sm"
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1 text-xs',
                    ITEM_TONE[item.status]
                  )}
                >
                  {item.status === 'RUNNING' && (
                    <RefreshCw
                      aria-hidden="true"
                      className="size-3 motion-safe:animate-spin"
                    />
                  )}
                  {ITEM_STATUS_LABEL[item.status]}
                </Typography>
              </li>
            ))}
          </ul>
        )}

        {waiting.length > 0 && (
          <ul className="max-h-80 overflow-y-auto border-t bg-muted/20">
            {waiting.map(({ item, index }) => (
              <li key={item.id} className="flex items-center gap-2 px-3 py-2">
                <Clock
                  aria-hidden="true"
                  className="size-3 shrink-0 text-muted-foreground"
                />
                <Typography
                  as="span"
                  variant="body-sm"
                  className="text-xs text-muted-foreground"
                >
                  {`${index === 0 ? 'Next' : 'Then'}: ${deals(item.total)}, started by ${item.startedBy.name}`}
                </Typography>
              </li>
            ))}
          </ul>
        )}

        {(state === 'failures' || state === 'stopped') && (
          <div className="flex max-h-80 flex-col gap-2 overflow-y-auto border-t px-3 py-2.5">
            {state === 'stopped' && (
              <div className="flex flex-wrap items-center gap-3">
                <Typography
                  as="span"
                  variant="body-sm"
                  className="text-primary"
                >
                  {`The send stopped after ${batch.total - rest} of ${batch.total}. The other ${rest} were not sent.`}
                </Typography>
                {canSend && rest > 0 && (
                  <Button
                    type="button"
                    size="sm"
                    disabled={retry.isPending}
                    aria-busy={retry.isPending}
                    onClick={() => retryAs('UNSENT')}
                  >
                    {`Send the other ${rest}`}
                  </Button>
                )}
              </div>
            )}
            {batch.failures.length > 0 && (
              <BatchFailurePanel
                failures={batch.failures}
                sent={batch.sent}
                duplicates={batch.duplicates}
                onRetry={canSend ? () => retryAs('FAILED') : undefined}
                retrying={retry.isPending}
              />
            )}
            {!canSend && (
              <Typography
                as="p"
                variant="body-sm"
                className={cn('text-xs', MUTED_TEXT)}
              >
                Only people who can send to DealCloud can retry.
              </Typography>
            )}
          </div>
        )}

        {stale && (
          <div className="flex items-center gap-2 border-t px-3 py-2">
            <TriangleAlert
              aria-hidden="true"
              className="size-3 shrink-0 text-amber-600"
            />
            <Typography
              as="span"
              variant="body-sm"
              className="min-w-0 flex-1 text-xs text-muted-foreground"
            >
              {`Not updating. Last checked ${relativeTime(now, active.errorUpdatedAt)}.`}
            </Typography>
            <Button
              variant="ghost"
              size="sm"
              className={PANEL_ACTION}
              onClick={() => void active.refetch()}
            >
              Try again
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
